import { getStartupName, type OnboardingAnswers } from './onboardingConfig';
const escape = (text: string) => text.replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;').replaceAll('"', '&quot;');

export function renderGuidedCommitment(answers: OnboardingAnswers) {
  const fingerprint = `<g fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round">
    <path d="M24 30c10-13 30-13 40 0M18 40c-1-27 53-32 55-1M14 51c-1-11 0-16 4-22M78 50c1-7 0-14-3-20"/>
    <path d="M24 55V43c0-26 41-26 41 0v17M31 65V43c0-17 27-17 27 0v24M38 76c3-11 0-25 0-32 0-9 13-9 13 0v22c0 10-2 15-5 20"/>
    <path d="M44 45v18c0 13-2 20-7 26M25 61c0 11-2 18-6 24M64 68c0 10-2 17-5 23M17 59c0 6-1 12-3 17M72 56v9c0 9-1 16-4 24"/>
  </g>`;
  return `<div class="question-wrap guided-commitment">
    <h2 class="question-title">Make it official.</h2>
    <p class="question-body">One focused session. One step forward for ${escape(getStartupName(answers))}.</p>
    <div class="guided-hold-stage">
      <button class="guided-hold-button" type="button" aria-label="Hold for three seconds to commit" aria-describedby="guided-hold-hint">
        <svg viewBox="0 0 92 100" aria-hidden="true">${fingerprint}</svg>
        <svg class="guided-hold-ink" viewBox="0 0 92 100" aria-hidden="true">${fingerprint}</svg>
      </button>
    </div>
    <strong class="guided-hold-label">I’m ready to start</strong>
    <p id="guided-hold-hint" class="guided-hold-hint">Hold for 3 seconds. Release to cancel.</p>
    <button class="guided-commitment-alternative" type="button" data-action="guided-commitment-choices">Use the question instead</button>
  </div>`;
}

/** Requires one uninterrupted hold. Losing focus, leaving the target or releasing cancels. */
export function mountGuidedCommitment(button: HTMLButtonElement, onComplete: () => void) {
  let frame = 0;
  let started = 0;
  let active: 'pointer' | 'keyboard' | null = null;
  let pointerId: number | null = null;
  let completed = false;
  const label = button.closest('.guided-commitment')?.querySelector('.guided-hold-label');
  const reset = () => {
    cancelAnimationFrame(frame); frame = 0; active = null; pointerId = null;
    button.style.setProperty('--hold-progress', '0%');
    button.classList.remove('is-holding');
    if (label && !completed) label.textContent = 'I’m ready to start';
  };
  const tick = (now: number) => {
    if (!active || completed) return;
    const progress = Math.min(1, (now - started) / 3000);
    button.style.setProperty('--hold-progress', `${progress * 100}%`);
    if (progress === 1) {
      completed = true; active = null; frame = 0;
      if (label) label.textContent = 'Let’s get to work.';
      onComplete();
    } else frame = requestAnimationFrame(tick);
  };
  const start = (kind: 'pointer' | 'keyboard') => {
    if (active || completed) return;
    active = kind; started = performance.now(); button.classList.add('is-holding');
    if (label) label.textContent = 'Keep holding…';
    frame = requestAnimationFrame(tick);
  };
  const down = (event: PointerEvent) => {
    if (event.button !== 0 || !event.isPrimary || active) return;
    event.preventDefault(); pointerId = event.pointerId;
    button.focus({ preventScroll: true });
    try { button.setPointerCapture(event.pointerId); } catch { /* Pointer may already have ended. */ }
    start('pointer');
  };
  const move = (event: PointerEvent) => {
    if (active !== 'pointer' || pointerId !== event.pointerId) return;
    const rect = button.getBoundingClientRect();
    if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) reset();
  };
  const up = (event: PointerEvent) => { if (active === 'pointer' && event.pointerId === pointerId) reset(); };
  const keyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape') reset();
    if (event.key !== ' ' && event.key !== 'Enter') return;
    event.preventDefault(); if (!event.repeat) start('keyboard');
  };
  const keyUp = (event: KeyboardEvent) => { if (active === 'keyboard' && (event.key === ' ' || event.key === 'Enter')) { event.preventDefault(); reset(); } };
  const visibility = () => { if (document.hidden) reset(); };
  const contextMenu = (event: Event) => event.preventDefault();
  button.addEventListener('pointerdown', down);
  button.addEventListener('pointermove', move);
  button.addEventListener('pointerup', up);
  button.addEventListener('pointercancel', reset);
  button.addEventListener('lostpointercapture', reset);
  button.addEventListener('keydown', keyDown);
  button.addEventListener('keyup', keyUp);
  button.addEventListener('blur', reset);
  button.addEventListener('contextmenu', contextMenu);
  window.addEventListener('blur', reset);
  document.addEventListener('visibilitychange', visibility);
  return { destroy() {
    reset();
    button.removeEventListener('pointerdown', down);
    button.removeEventListener('pointermove', move);
    button.removeEventListener('pointerup', up);
    button.removeEventListener('pointercancel', reset);
    button.removeEventListener('lostpointercapture', reset);
    button.removeEventListener('keydown', keyDown);
    button.removeEventListener('keyup', keyUp);
    button.removeEventListener('blur', reset);
    button.removeEventListener('contextmenu', contextMenu);
    window.removeEventListener('blur', reset);
    document.removeEventListener('visibilitychange', visibility);
  } };
}
